import { Stack, useMantineTheme } from '@mantine/core'
import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import UserDetails from '@/components/Details/UserDetails/UserDetails'
import classes from '@/components/Discover/Feed/MembersList.module.css'
import GeneralOptionsMenu from '@/components/GeneralOptionsMenu'
import { useGovernanceContext } from '@/contexts/GovernanceContext'
import { useModalContext } from '@/contexts/ModalContext'
import { CandidateStatus } from '@/hooks/useGovernance'
import useProfilePath from '@/hooks/useProfilePath'
import { useAuth } from '@/plugins/auth'
import { bronzeColor, silverColor } from '@/utils/utils'

import NominatedBy from './NominatedBy'
import { ActionProps } from './NomineesList'

interface NomineeCardProps<T extends ActionProps = ActionProps> {
  nominee: string
  nominator: string
  status: CandidateStatus
  cid: string
  phase: string
  index: number
  isBreakpoint: boolean | undefined
  isMobile: boolean | undefined
  isResults?: boolean
  action?: React.FC<T>
  actionProps?: T
}

const NomineeCard = <T extends ActionProps = ActionProps>({
  nominee,
  nominator,
  status,
  cid,
  phase,
  index,
  isBreakpoint,
  isMobile,
  isResults,
  action,
  actionProps,
}: NomineeCardProps<T>) => {
  const theme = useMantineTheme()
  const { address } = useAuth()
  const navigate = useNavigate()
  const profilePath = useProfilePath(nominee)
  const { openModal } = useModalContext()
  const { electionData } = useGovernanceContext()
  const handleOpenApplicationModal = useCallback(() => {
    openModal('viewApplication', { nominee })
  }, [nominee, openModal])

  const campaignLink = useMemo(() => {
    const key = `campaignLinks-${electionData.idx}`
    const sessionCampaignLinks = sessionStorage.getItem(key)
    const links = sessionCampaignLinks ? new Map(JSON.parse(sessionCampaignLinks)) : new Map()
    return links.get(nominee) || ''
  }, [electionData.idx, nominee])

  const links = useMemo(() => {
    const baseLinks = [
      {
        label: 'View Profile',
        action: () => {
          window.open(profilePath, '_blank')
        },
      },
    ]
    if ((phase === 'nomination' || phase === 'campaign') && cid !== '' && address === nominee) {
      baseLinks.push({ label: 'Edit Application', action: () => navigate('/application') })
    }
    if (cid !== '') {
      baseLinks.push({ label: 'View Application', action: handleOpenApplicationModal })
    }
    if ((phase === 'voting' || phase === 'results') && campaignLink) {
      baseLinks.push({
        label: 'View Campaign',
        action: () => window.open(campaignLink, '_blank'),
      })
    }
    return baseLinks
  }, [profilePath, handleOpenApplicationModal, address, nominee, status, phase, campaignLink])

  return (
    <>
      <Stack
        className={classes.memberNoHover}
        style={{
          width: '100%',
          position: 'relative',
          justifyContent: 'space-between',
          flexDirection: isBreakpoint ? 'column' : 'row',
          alignItems: isBreakpoint ? 'flex-start' : 'center',
          borderColor:
            isResults && index === 0
              ? theme.colors.barChartYellow[0]
              : isResults && index === 1
                ? silverColor
                : isResults && index === 2
                  ? bronzeColor
                  : status === 'REMOVED'
                    ? theme.colors.notification[0]
                    : address === nominee
                      ? theme.colors.l1Primary[0]
                      : '',
          backgroundColor:
            isResults && index === 0
              ? 'rgba(255, 242, 0, 0.2)'
              : status === 'REMOVED'
                ? theme.colors.background2[0]
                : '',
        }}
      >
        <UserDetails
          pfpSize="60px"
          userId={nominee}
          showApplication
          width={isMobile ? '260px' : '330px'}
          addInfo={<NominatedBy nominator={nominator} />}
        />
        <div style={{ width: '100%' }} onClick={e => e.stopPropagation()}>
          {action && actionProps && action(actionProps)}
        </div>
        <Stack
          style={
            isBreakpoint
              ? { position: 'absolute', top: '10px', right: '10px' }
              : {
                  width: isBreakpoint ? '100%' : 'fit-content',
                  alignItems: 'flex-end',
                }
          }
        >
          <GeneralOptionsMenu
            onClick={e => e.stopPropagation()}
            links={links}
            vertical={isBreakpoint}
            style={{
              backgroundColor: 'rgba(227, 221, 221, 0.2) ',
            }}
          />
        </Stack>
      </Stack>
    </>
  )
}

export default NomineeCard
